import {
    Injectable, inject
} from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
    BulkResponse, BulkUtil, CreateRequest, DeleteRequest, DeleteResponse,
    GetResponse, RequestUtil, SearchResponse, SubmitResponse, UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    ISearchSort, ModelAction
} from "@shopliftr/common-js/data";
import {
    ApiKey, ApiKeyOperation, ApiKeySearchFilterOptions, ApiKeyUsagePlanSubmission, ApiUsagePlan
} from "@shopliftr/common-js/admin";
import { IFullAccessInternalApiClientService } from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { ListUtil } from "../../../../list/util/list-util";

/**
 * API Client Service for ApiKey entities.
 *
 * Implements full CRUD operations for ApiKey entities via the Internal API v2.
 * Delegates HTTP communication to InternalApiRequestService while encapsulating
 * ApiKey-specific request/response handling.
 *
 * @example
 * ```typescript
 * private readonly _apiKeyApiClientService = inject(ApiKeyApiClientService);
 *
 * this._apiKeyApiClientService.get$('api-key-123').subscribe(apiKey => console.log(apiKey));
 * this._apiKeyApiClientService.getAll$(new ApiKeySearchFilterOptions()).subscribe(apiKeys => console.log(apiKeys));
 * this._apiKeyApiClientService.create$(apiKey).subscribe(created => console.log(created));
 * this._apiKeyApiClientService.delete$(apiKey.key, apiKey.esVersion).subscribe(() => console.log('deleted'));
 * ```
 *
 * @pattern IFullAccessInternalApiClientService
 * @providedIn root
 * @implements {IFullAccessInternalApiClientService<ApiKey>}
 */
@Injectable({ providedIn: "root" })
export class ApiKeyApiClientService implements IFullAccessInternalApiClientService<ApiKey> {

    readonly basePath = "api-key";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieve multiple ApiKey entities by their IDs in a single bulk request.
     *
     * @param ids Array of unique identifiers to retrieve
     * @returns Observable emitting a BulkResponse containing the requested entities
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<ApiKey>(id, ApiKey.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieve a single ApiKey by its unique identifier.
     *
     * @param id The unique identifier of the entity to retrieve
     * @returns Observable emitting the ApiKey entity
     */
    get$(id: string): Observable<ApiKey> {
        const request = RequestUtil.createGetRequest<void>(id, ApiKey.modelName, ModelAction.ClientGet);
        return this._internalApiRequestService.get$<ApiKey>(request, this.basePath).pipe(
            map((response: GetResponse<ApiKey>) => response.payload)
        );
    }

    /**
     * Retrieve all ApiKey entities matching the provided filter criteria.
     *
     * @param filterOptions The search criteria to apply
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable emitting an array of all matching entities
     */
    getAll$(filterOptions: ApiKeySearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<ApiKey>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, ApiKey.modelName, undefined, undefined, filterOptions, sort
        );
        return this._internalApiRequestService.getAll$(request, this.basePath);
    }

    /**
     * Search for ApiKey entities with pagination support.
     *
     * @param filterOptions The search criteria to apply
     * @param page Zero-based page index for pagination (optional)
     * @param pageSize Number of items per page (optional)
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable emitting search results with pagination metadata
     */
    search$(
        filterOptions: ApiKeySearchFilterOptions, page?: number, pageSize?: number, sort?: Array<ISearchSort>
    ): Observable<SearchResponse<ApiKey>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, ApiKey.modelName, ListUtil.getFromValue(page, pageSize), pageSize, filterOptions, sort
        );
        return this._internalApiRequestService.search$(request, this.basePath);
    }

    /**
     * Create a new ApiKey entity.
     *
     * @param fields The ApiKey model containing the field values for the new entity
     * @returns Observable emitting the created ApiKey entity
     */
    create$(fields: ApiKey): Observable<ApiKey> {
        const createRequest = RequestUtil.createCreateRequest<ApiKey, void>(
            ModelAction.ClientCreate, fields.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath)
            .pipe(map((response) => response.payload));
    }

    /**
     * Create multiple ApiKey entities in a single bulk request.
     *
     * @param items Array of create requests for api keys to create
     * @returns Observable emitting a BulkResponse with results of each create operation
     */
    bulkCreate$(items: Array<CreateRequest<ApiKey, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(items.map((item) => BulkUtil.createRequestItem(item)));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Update an ApiKey entity by ID and version.
     *
     * @param id Unique identifier of the api key to update
     * @param version Version for optimistic concurrency control
     * @param fields ApiKey model containing the updated field values
     * @param removeFields Optional array of field names to remove
     * @returns Observable emitting the updated ApiKey entity
     */
    update$(id: string, version: number, fields: ApiKey, removeFields?: Array<string>): Observable<ApiKey> {
        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<ApiKey> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath)
            .pipe(map((response) => response.payload));
    }

    /**
     * Update an ApiKey entity by diffing original and updated models.
     *
     * @param original The original ApiKey model
     * @param updated The ApiKey model with changes applied
     * @returns Observable emitting the updated ApiKey entity
     */
    updateWithChanges$(original: ApiKey, updated: ApiKey): Observable<ApiKey> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Update multiple ApiKey entities in a single bulk request.
     *
     * @param requests Array of update requests
     * @returns Observable emitting a BulkResponse with results of each update operation
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(requests.map((request) => BulkUtil.createRequestItem(request)));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Delete an ApiKey entity by ID and version.
     *
     * @param id Unique identifier of the api key to delete
     * @param version Version for optimistic concurrency control
     * @returns Observable emitting the DeleteResponse confirming deletion
     */
    delete$(id: string, version: number): Observable<DeleteResponse> {
        const deleteRequest = RequestUtil.createDeleteRequest<ApiKey>(
            id, ApiKey.modelName, ModelAction.ClientDelete, version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    /**
     * Delete multiple ApiKey entities in a single bulk request.
     *
     * @param items Array of delete requests
     * @returns Observable emitting a BulkResponse with results of each delete operation
     */
    bulkDelete$(items: Array<DeleteRequest<unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(items.map((item) => BulkUtil.createRequestItem(item)));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieve the list of usage plans available for api keys.
     *
     * @returns Observable emitting an array of available ApiUsagePlan entities
     */
    listUsagePlans$(): Observable<Array<ApiUsagePlan>> {
        const request = RequestUtil.createSubmitRequest(new ApiKeyUsagePlanSubmission());
        // submit$'s response generic is constrained to IValidatedModel<R> | void, which an Array<ApiUsagePlan>
        // payload does not satisfy. The payload shape is still Array<ApiUsagePlan> at runtime, so it is safe
        // to cast through unknown here to expose the correctly-typed Observable to callers.
        return this._internalApiRequestService.submit$<ApiKeyUsagePlanSubmission, void>(
            request, `${this.basePath}/list-usage-plans`
        ).pipe(map((response: SubmitResponse<void>) => response.payload as unknown as Array<ApiUsagePlan>));
    }

    /**
     * Activate an ApiKey entity by ID.
     *
     * @param id Unique identifier of the api key to activate
     * @returns Observable emitting void upon successful activation
     */
    activate$(id: string): Observable<void> {
        const request = RequestUtil.createCustomOpRequest<void>(id, ApiKey.modelName, ApiKeyOperation.Activate);
        return this._internalApiRequestService.customOp$(request, this.basePath).pipe(map(() => {}));
    }

    /**
     * Deactivate an ApiKey entity by ID.
     *
     * @param id Unique identifier of the api key to deactivate
     * @returns Observable emitting void upon successful deactivation
     */
    deactivate$(id: string): Observable<void> {
        const request = RequestUtil.createCustomOpRequest<void>(id, ApiKey.modelName, ApiKeyOperation.Deactivate);
        return this._internalApiRequestService.customOp$(request, this.basePath).pipe(map(() => {}));
    }
}
